import React, {ReactNode} from 'react';
import {StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import rewardsTheme from '../../../theme/rewardsTheme';

import SignUpFlowContent, {SignUpEmailOutcome} from './SignUpFlowContent';

/**
 * The two sign-up entry screens and the transition between them. The account
 * lookup is stubbed per story, because the flow takes it as a prop rather than
 * calling the API itself.
 *
 * Every story renders through its own `render` and takes no story args, because
 * the in-app browser at `app/screens/StorybookScreen.web.tsx` calls
 * `story.render()` with no arguments and `decorator(Story)` with no context.
 */
const MobileFrame = ({children}: {children: ReactNode}) => (
  <View style={styles.mobileFrame}>{children}</View>
);

const DesktopFrame = ({children}: {children: ReactNode}) => (
  <View style={styles.desktopFrame}>{children}</View>
);

/** Anything at this domain is treated as an account that already exists. */
const EXISTING_DOMAIN = '@gmail.com';

const checkEmailByDomain = (email: string): SignUpEmailOutcome =>
  email.toLowerCase().endsWith(EXISTING_DOMAIN) ? 'existing' : 'new';

const alwaysExisting = (): SignUpEmailOutcome => 'existing';

/** A lookup that takes 600ms, to show the flow holding the press until it lands. */
const slowCheck = (email: string): Promise<SignUpEmailOutcome> =>
  new Promise(resolve => setTimeout(() => resolve(checkEmailByDomain(email)), 600));

const logNewAccount = (email: string) => console.log('New account for', email);
const logLogIn = (email: string) => console.log('Log in', email);
const logForgot = (email: string) => console.log('Forgot Password for', email);
const logClose = () => console.log('Close pressed');

const meta = {
  title: 'Screens/Sign Up/Flow',
  component: SignUpFlowContent,
  decorators: [
    Story => (
      <ThemeProvider theme={rewardsTheme}>
        <Story />
      </ThemeProvider>
    ),
  ],
  parameters: {
    layout: 'fullscreen',
    docs: {
      description: {
        component:
          'Sign-up entry flow: email, then password when the address belongs to an existing account.\n\n' +
          '- The account lookup arrives as the `onCheckEmail` prop; nothing here calls the API.\n' +
          "- 'existing' cross-fades to the password screen; the back arrow cross-fades back and clears the password.\n" +
          "- 'new' hands the address to `onNewAccount` and the flow stays on the email screen.\n" +
          '- The transition is a 120ms fade out and a 180ms fade in with a 12px slide. Under an OS reduce-motion setting the slide is dropped and only the fade runs.',
      },
    },
  },
  args: {
    onCheckEmail: checkEmailByDomain,
    onRewardsSignUp: () => {},
    onNewAccount: logNewAccount,
    onLogIn: logLogIn,
    onForgotPassword: logForgot,
    onClose: logClose,
  },
} satisfies Meta<typeof SignUpFlowContent>;

export default meta;

type Story = StoryObj<typeof meta>;

/**
 * Type an address ending `@gmail.com` and press NEXT to reach the password
 * screen; any other domain logs the new-account path instead.
 */
export const MobileTransition: Story = {
  name: 'Mobile — Existing email transitions to password',
  render: () => (
    <MobileFrame>
      <SignUpFlowContent
        onCheckEmail={checkEmailByDomain}
        onRewardsSignUp={() => {}}
        onNewAccount={logNewAccount}
        onLogIn={logLogIn}
        onForgotPassword={logForgot}
        onClose={logClose}
      />
    </MobileFrame>
  ),
};

/** Every address is treated as existing, so any valid email reaches the password screen. */
export const MobileAlwaysExisting: Story = {
  name: 'Mobile — Every email is an existing account',
  render: () => (
    <MobileFrame>
      <SignUpFlowContent
        onCheckEmail={alwaysExisting}
        onRewardsSignUp={() => {}}
        onNewAccount={logNewAccount}
        onLogIn={logLogIn}
        onForgotPassword={logForgot}
        onClose={logClose}
        initialEmail="johnsmith@gmail.com"
      />
    </MobileFrame>
  ),
};

/** A 600ms lookup: repeated NEXT presses collapse into one transition. */
export const MobileSlowLookup: Story = {
  name: 'Mobile — Slow lookup (600ms)',
  render: () => (
    <MobileFrame>
      <SignUpFlowContent
        onCheckEmail={slowCheck}
        onRewardsSignUp={() => {}}
        onNewAccount={logNewAccount}
        onLogIn={logLogIn}
        onForgotPassword={logForgot}
        onClose={logClose}
        initialEmail="johnsmith@gmail.com"
      />
    </MobileFrame>
  ),
};

/** The same flow on the desktop layout: wide hero, then the reading-width form. */
export const DesktopTransition: Story = {
  name: 'Desktop — Existing email transitions to password',
  render: () => (
    <DesktopFrame>
      <SignUpFlowContent
        onCheckEmail={checkEmailByDomain}
        onRewardsSignUp={() => {}}
        onNewAccount={logNewAccount}
        onLogIn={logLogIn}
        onForgotPassword={logForgot}
        onClose={logClose}
      />
    </DesktopFrame>
  ),
};

const styles = StyleSheet.create({
  mobileFrame: {
    width: 375,
    height: 812,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
  },
  desktopFrame: {
    width: '100%',
    minHeight: 700,
    backgroundColor: '#ffffff',
  },
});
